import { INGXLoggerConfig, NGXLogger, NgxLoggerLevel } from 'ngx-logger';
import { JmkwEnvironment } from '@jeppesen-foreflight/jmkw-types';

export const JMKW_LOG_LEVEL_STORAGE_KEY = 'jmkw.logLevel';

const VALID_LOG_LEVELS = [
  'TRACE',
  'DEBUG',
  'INFO',
  'LOG',
  'WARN',
  'ERROR',
  'FATAL',
  'OFF'
] as const;

type ValidLogLevel = (typeof VALID_LOG_LEVELS)[number];

/**
 * Builds the initial NGXLogger config by reading the log level from localStorage.
 * Used as the TOKEN_LOGGER_CONFIG factory — called at DI construction time.
 *
 * To override the JMKW log level at runtime:
 *   localStorage.setItem('jmkw.logLevel', 'INFO')  // then reload
 *   localStorage.removeItem('jmkw.logLevel')        // to restore default
 */
export function buildLoggerConfig(
  config: Partial<JmkwEnvironment> | null
): INGXLoggerConfig {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(JMKW_LOG_LEVEL_STORAGE_KEY);
  } catch {
    console.warn('[buildLoggerConfig] localStorage unavailable — using environment log level');
  }
  const upperValue = stored?.toUpperCase() ?? null;
  const level =
    upperValue !== null && VALID_LOG_LEVELS.includes(upperValue as ValidLogLevel)
      ? NgxLoggerLevel[upperValue as keyof typeof NgxLoggerLevel]
      : config?.logLevel
        ? NgxLoggerLevel[config.logLevel]
        : NgxLoggerLevel.DEBUG;
  return { level, serverLogLevel: NgxLoggerLevel.ERROR };
}

export function configureLogLevel(
  config: Partial<JmkwEnvironment>,
  logger: NGXLogger
) {
  return () => {
    let overrideLevel: NgxLoggerLevel | null = null;

    let stored: string | null = null;
    try {
      stored = localStorage.getItem(JMKW_LOG_LEVEL_STORAGE_KEY);
    } catch {
      console.warn('[configureLogLevel] localStorage unavailable — using environment log level');
    }

    if (stored !== null) {
      const upperValue = stored.toUpperCase();
      if (VALID_LOG_LEVELS.includes(upperValue as ValidLogLevel)) {
        overrideLevel = NgxLoggerLevel[upperValue as keyof typeof NgxLoggerLevel];
        logger.info(
          `[configureLogLevel] Log level overridden via localStorage (${JMKW_LOG_LEVEL_STORAGE_KEY}): ${upperValue}`
        );
      } else {
        logger.warn(
          `[configureLogLevel] Invalid log level in localStorage (${JMKW_LOG_LEVEL_STORAGE_KEY}): '${stored}' — ignoring`
        );
      }
    }

    const newLevel: NgxLoggerLevel =
      overrideLevel !== null
        ? overrideLevel
        : config?.logLevel
          ? NgxLoggerLevel[config.logLevel]
          : NgxLoggerLevel.DEBUG;

    const loggerConfig = logger.getConfigSnapshot();
    loggerConfig.level = newLevel;
    logger.updateConfig(loggerConfig);

    logger.trace(
      `[configureLogLevel]: Updated log config to ${JSON.stringify(loggerConfig)}`
    );
  };
}
